import { z } from 'zod'
import { RoomModel } from './RoomModel.js'


export const attendeeSchema = z.object({
    displayName: z.string().optional().describe('The name of the attendee'),
    email: z.string().email().describe('The email address of the attendee'),
    self: z.boolean().default(false).describe('Indicates if the attendee is the user themselves'),
    organizer: z.boolean().default(false).describe('Indicates if the attendee is the organizer'),
    responseStatus: z.string().optional().describe('The response status of the attendee'),
})

export const BookingSchema = z.object({
    id: z.string().describe('The unique identifier of the booking'),
    roomId: z.string().describe('The ID of the room booked'),
    userId: z.string().describe('The ID of the user who made the booking'),
    userName: z.string().describe('The name of the user who made the booking'),
    organizerName: z.string().describe('The name of the organizer, if applicable'),
    organizerEmail: z.string().email().describe('The email of the organizer, if applicable'),
    startTime: z.date().describe('The start time of the booking'),
    endTime: z.date().describe('The end time of the booking'),
    duration: z.number().int().min(1).describe('The duration of the booking in minutes'),
    isVirtual: z.boolean().describe('Indicates if the booking is virtual'),
    attendees: z.array(attendeeSchema).describe('List of attendees for the booking'),
})

export type Booking = z.infer<typeof BookingSchema>
export type Attendee = z.infer<typeof attendeeSchema>

export class BookingModel implements Booking {

    public id: string
    public roomId: string
    public userId: string
    public userName: string
    public startTime: Date
    public endTime: Date
    public duration: number
    public isVirtual: boolean
    public organizerName: string
    public organizerEmail: string
    public attendees: Attendee[] = []
    public room: RoomModel

    constructor(booking: Booking, room: RoomModel) {
        this.id = booking.id
        this.roomId = booking.roomId
        this.userId = booking.userId
        this.userName = booking.userName
        this.startTime = new Date(booking.startTime)
        this.endTime = new Date(booking.endTime)
        this.duration = booking.duration
        this.isVirtual = booking.isVirtual
        this.organizerName = booking.organizerName
        this.organizerEmail = booking.organizerEmail
        this.attendees = booking.attendees
        this.room = room
    }

    canDelete(organizerEmail: string) {
        return this.organizerEmail === organizerEmail
    }

    get isRoomEvent(): boolean {
        return this.room.email === this.organizerEmail
    }

    get organizerAttendee(): Attendee | undefined {
        return this.attendees.find(attendee => attendee.self)
    }

    get roomAttendee(): Attendee | undefined {
        return this.attendees.find(attendee => attendee.email === this.room.email)
    }

    get attendeeCount(): number {
        return Math.max(0, this.attendees.length - (this.organizerAttendee ? 1 : 0) - (this.roomAttendee ? 1 : 0))
    }

}
